import { NEW_PRESETS_URL } from '@/consts/urls';
import { NEW_TAB_SLUG } from '@/store/homeTabs/types';
import { fetchLibraryData } from './utils';

import type { LibraryItem, LibrarySection, Media, PresetOwner } from '@/types';
import type { PreviewMediaType } from '@/types/media';

interface RawMedia {
  url?: string | null;
  width?: number | null;
  height?: number | null;
  provider_metadata?: {
    width?: number | null;
    height?: number | null;
    poster?: string | null;
  } | null;
}

interface FlowLanding {
  id: number;
  documentId: string;
  slug: string;
  type: PreviewMediaType;
  title?: string;
  description?: string;
  video?: { media?: RawMedia | null }[];
  image?: { media?: RawMedia | null }[];
  flow?: { flowId?: string; presetCreditCount?: number | null } | null;
  owner?: PresetOwner | null;
}

type ResponseType = {
  data: FlowLanding[];
};

const toMedia = (raw?: RawMedia | null): Media | null => {
  if (!raw?.url) return null;

  // Images carry dimensions top-level; videos expose them (and the poster)
  // only through `provider_metadata`.
  const meta = raw.provider_metadata;

  return {
    url: raw.url,
    width: raw.width ?? meta?.width ?? 0,
    height: raw.height ?? meta?.height ?? 0,
    ...(meta?.poster ? { poster: meta.poster } : {}),
  };
};

const toLibraryItem = (entry: FlowLanding): LibraryItem | null => {
  const media = toMedia(entry.video?.[0]?.media ?? entry.image?.[0]?.media);
  if (!media) return null;

  return {
    id: entry.id,
    documentId: entry.documentId,
    slug: entry.slug,
    type: entry.type,
    title: entry.title,
    description: entry.description,
    media,
    presetCreditCount: entry.flow?.presetCreditCount ?? null,
    // Carried so the feed can quote the flow's credit cost from the calculator
    // API; the editorial grid sections already ship `flowId` on every item.
    ...(entry.flow?.flowId ? { flowId: entry.flow.flowId } : {}),
    ...(entry.owner ? { owner: entry.owner } : {}),
  };
};

const getNewPresets = async (): Promise<LibrarySection[] | null> => {
  return fetchLibraryData({
    url: NEW_PRESETS_URL,
    transform: (response) => {
      const data = response as ResponseType;
      if (!data?.data) return null;

      const items = data.data
        .map(toLibraryItem)
        .filter((item): item is LibraryItem => item !== null);

      return [{ title: 'New', slug: NEW_TAB_SLUG, items }];
    },
  });
};

export default getNewPresets;
